import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navigation } from "./components/navigation";
import { PromotionalBanner } from "./components/promotionalbanner";
import { Header } from "./components/header";
import { Solutions } from "./components/solutions";
import { About } from "./components/about";
import { Services } from "./components/services";
import { Gallery } from "./components/gallery";
import { Testimonials } from "./components/testimonials";
import { Promos } from "./components/promos";
import { Contact } from "./components/contact";
import ServicePage from "./components/ServicePage";
import WinterSavingsPage from "./components/WinterSavingsPage";
import JsonData from "./data/data.json";
import SeoConfig from "./data/seo-config.json";
import SmoothScroll from "smooth-scroll";
import { PROMO_FEATURES_ENABLED } from "./config/promoConfig";
import "./App.css";

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
  offset: function(anchor, toggle) {
    // Calculate offset to land at the very top of each section (just past navbar/banner)
    const menuBar = document.getElementById('menu');
    const menuHeight = menuBar ? menuBar.offsetHeight : 80; // fallback to 80px
    const bannerHeight = PROMO_FEATURES_ENABLED ? 50 : 0;

    // Just clear the fixed elements, don't add section padding
    return menuHeight + bannerHeight;
  }
});

const HomePage = ({ landingPageData, seoConfig }) => {
  const { meta, openGraph, twitter } = seoConfig;

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "CyberElectrix",
    "url": "https://cyberelectrix.co.nz",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://cyberelectrix.co.nz/?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["#header .intro-text h1", "#header .intro-text p", "#about .about-text p"]
    },
    "url": "https://cyberelectrix.co.nz/"
  };

  // Fallbacks for optional fields the LLM may omit
  const ogImage = openGraph.image || "https://cyberelectrix.co.nz/img/og-image.jpg";
  const ogUrl = openGraph.url || "https://cyberelectrix.co.nz";
  const ogSiteName = openGraph.siteName || "CyberElectrix";
  const twImage = twitter.image || "https://cyberelectrix.co.nz/img/og-image.jpg";

  return (
    <div>
      <Helmet>
        {/* Primary meta */}
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={(meta.keywords || []).join(", ")} />

        {/* Canonical */}
        <link rel="canonical" href="https://cyberelectrix.co.nz/" />

        {/* Open Graph */}
        <meta property="og:title" content={openGraph.title} />
        <meta property="og:description" content={openGraph.description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content={openGraph.type || "website"} />
        <meta property="og:site_name" content={ogSiteName} />

        {/* Twitter Card */}
        <meta name="twitter:card" content={twitter.card || "summary_large_image"} />
        <meta name="twitter:title" content={twitter.title} />
        <meta name="twitter:description" content={twitter.description} />
        <meta name="twitter:image" content={twImage} />

        {/* JSON-LD: WebSite with SearchAction */}
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>

        {/* JSON-LD: Speakable — marks hero and about text for voice/AI reading */}
        <script type="application/ld+json">
          {JSON.stringify(speakableSchema)}
        </script>

        {/* JSON-LD: Dynamic FAQPage from SEO engine (seo-config.json → updated weekly by crawlbot) */}
        {seoConfig.jsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(seoConfig.jsonLd)}
          </script>
        )}
      </Helmet>
      <PromotionalBanner />
      <Navigation />
      <Header data={landingPageData.Header} />
      <About data={landingPageData.About} partners={landingPageData.Partners} />
      <Services data={landingPageData.Services} />
      <Solutions data={landingPageData.Features} />
      <Gallery data={landingPageData.Gallery} />
      <Testimonials data={landingPageData.Testimonials} />
      <Promos data={landingPageData.Promos} />
      <Contact data={landingPageData.Contact} />
    </div>
  );
};

const App = () => {
  const [landingPageData, setLandingPageData] = useState({});
  useEffect(() => {
    setLandingPageData(JsonData);
  }, []);

  return (
    <Routes>
      <Route
        path="/"
        element={<HomePage landingPageData={landingPageData} seoConfig={SeoConfig} />}
      />
      <Route path="/services/:serviceSlug" element={<ServicePage />} />
      <Route path="/how-to" element={<WinterSavingsPage />} />
      {/* Catch-all: redirect unknown paths to homepage */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
